import { Button, Dropdown, Menu, notification, Row, Switch, Table } from 'antd';
import './webhookListing.scss';
import {
  EyeIconHide2,
  EyeIconShow2,
  HistoryIcon,
  LockIcon,
  MenuIcon,
  RefreshIcon2,
} from 'components/Icons';
import { GET_ALL_WEBHOOKS } from 'graphql/queries/webhookQueries';
import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { WebhookModal } from './components';
import {
  CREATE_WEBHOOK,
  DELETE_WEBHOOK,
  REGENERATE_WEBHOOK_SECRET_KEY,
  UPDATE_WEBHOOK,
} from 'graphql/mutations/webhookMutations';
import { useState } from 'react';
import PopConfirm from 'components/PopConfirm';
import { EventList, WebhookHistory } from './components/webhookHistory';
import { GenerateToken } from './components/GenerateToken';
import { PopoverComponent } from 'pages/Proposal/components/Publish/modals/PublishModals/PublishedProposalModal/RecipientModals';
import { userVar } from 'graphql/cache';

export const WebhookListing = ({ setWebhookModal, webhookModal }) => {
  const [showModal, setShowModal] = useState(false);
  const [webhookHistoryModal, setWebhookHistoryModal] = useState(false);
  const [hideSecretKey, setHideSecretKey] = useState(true);
  const user = useReactiveVar(userVar);

  const isDisabled = user?.paymentStatus?.canLock || !!user?.teamId;

  const {
    data: { fetchUserWebhooks: { webhooksList = [], secretKey, _id: webhookId } = {} } = {},
    loading: fetchWebhookLoading,
    refetch: refetchWebhooks,
  } = useQuery(GET_ALL_WEBHOOKS, {
    fetchPolicy: 'network-only',
  });

  const [createWebhook, { loading: createWebhookLoading }] = useMutation(CREATE_WEBHOOK, {
    onCompleted: (data) => {
      setWebhookModal(false);
      refetchWebhooks();
      notification.success({
        message: 'Webhook created successfully',
      });
    },
  });

  const [updateWebhook, { loading: updateWebhookLoading }] = useMutation(UPDATE_WEBHOOK, {
    onCompleted: (data) => {
      setWebhookModal(false);
      refetchWebhooks();
      notification.success({
        message: 'Webhook updated successfully',
      });
    },
  });

  const [deleteWebhook, { loading: deleteWebhookLoading }] = useMutation(DELETE_WEBHOOK, {
    onCompleted: (data) => {
      refetchWebhooks();
      notification.success({
        message: 'Webhook deleted successfully',
      });
      setShowModal(false);
    },
  });

  const [regenerateWebhookSecretKey] = useMutation(REGENERATE_WEBHOOK_SECRET_KEY, {
    onCompleted: (data) => {
      refetchWebhooks();
      notification.success({
        message: 'Webhook secret key regenerated successfully',
      });
    },
  });

  const onActionClick = ({ key: action }, record) => {
    if (action === 'edit') {
      setWebhookModal({
        isEdit: true,
        data: record,
      });
    } else if (action === 'delete') {
      setShowModal(record);
    }
  };

  const menu = (record) => {
    return (
      <Menu onClick={(e) => onActionClick(e, record)} className="users-menu-items">
        <Menu.Item className="edit-text" key="edit">
          Edit
        </Menu.Item>
        <Menu.Item className="delete-text" key="delete">
          Delete
        </Menu.Item>
      </Menu>
    );
  };

  const onStatusChange = (checked, record) => {
    updateWebhook({
      variables: {
        webhookData: {
          webhookId,
          webhookItem: { ...record, status: checked },
        },
      },
    });
  };

  const onRegenerate = () => {
    regenerateWebhookSecretKey();
  };

  const webhookColumn = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      onCell: () => ({
        className: 'name-cell', // custom class for this column's td
      }),
      render: (text, record) => (
        <Row className="webhook-name" style={{ alignItems: 'center', gap: '8px' }}>
          <Switch
            defaultChecked={record.status}
            onChange={(checked) => onStatusChange(checked, record)}
            disabled={isDisabled}
          />
          {record.encrypted && <LockIcon style={{ marginRight: '-4px' }} />}
          <span>{record.name}</span>
        </Row>
      ),
    },
    {
      title: 'Event',
      dataIndex: 'events',
      key: 'events',
      onCell: () => ({
        className: 'events-cell', // custom class for this column's td
      }),
      width: '307px',
      render: (text, record) => <EventList events={record.events} />,
    },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      onCell: () => ({
        className: 'url-cell', // custom class for this column's td
      }),
    },
    ...(isDisabled
      ? []
      : [
          {
            title: '',
            dataIndex: '',
            key: '',
            onCell: () => ({
              className: 'action-cell', // custom class for this column's td
            }),
            align: 'right',
            render: (text, record) => (
              <Dropdown
                className="proposal-item-menu"
                overlay={() => menu(record)}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                trigger={['click']}>
                <a
                  className="ant-dropdown-link"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}>
                  <MenuIcon />
                </a>
              </Dropdown>
            ),
          },
        ]),
  ];

  return (
    <div className="webhook-listing-wrapper">
      <Row className="webhook-listing-header">
        <Row className="webhook-listing-header-title">
          <span>Webhooks</span>
          <p>
            Trigger real-time actions when key events happen - like when a proposal is signed or
            opened. Stay synced and automate your workflows effortlessly.
          </p>
        </Row>
        <Row className="webhook-listing-header-actions">
          <Button
            className="grey-btn webhook-history"
            icon={<HistoryIcon />}
            onClick={() => setWebhookHistoryModal(true)}>
            Webhook history
          </Button>
          {!isDisabled && (
            <Row className="webhook-key">
              <span className="webhook-key-label">Secret key:</span>
              <span className="webhook-key-value">
                {hideSecretKey ? '***************' : secretKey}
              </span>
              <Row style={{ alignItems: 'center', gap: '10px' }}>
                {hideSecretKey ? (
                  <EyeIconHide2 onClick={() => setHideSecretKey(false)} />
                ) : (
                  <EyeIconShow2 onClick={() => setHideSecretKey(true)} />
                )}
                <div className="divider"></div>
                <PopoverComponent
                  icon={<RefreshIcon2 />}
                  content={<GenerateToken onRegenerate={onRegenerate} onVisibleChange={() => {}} />}
                />
              </Row>
            </Row>
          )}
        </Row>
      </Row>
      <Table
        className="webhook-listing-table"
        columns={webhookColumn}
        dataSource={[...webhooksList]?.reverse() || []}
        scroll={{ x: 'max-content' }}
        pagination={false}
        loading={fetchWebhookLoading}
        rowKey={(record) => record.id}
      />

      {webhookModal && (
        <WebhookModal
          isOpen={webhookModal !== false}
          onCancel={() => setWebhookModal(false)}
          data={webhookModal}
          loading={createWebhookLoading || updateWebhookLoading}
          onUpdate={(data) => {
            updateWebhook({
              variables: {
                webhookData: {
                  webhookId,
                  webhookItem: data,
                },
              },
            });
          }}
          onCreate={(data) => {
            createWebhook({
              variables: { webhookItem: { ...data, status: true } },
            });
          }}
        />
      )}
      {webhookHistoryModal && (
        <WebhookHistory
          isOpen={webhookHistoryModal !== false}
          onCancel={() => setWebhookHistoryModal(false)}
          loading={createWebhookLoading || updateWebhookLoading}
        />
      )}
      {showModal && (
        <PopConfirm
          visible={showModal}
          title={'Are you sure to delete this webhook?'}
          closable={false}
          cancelText={'Cancel'}
          confirmText={'Delete'}
          onCancel={() => {
            setShowModal(false);
          }}
          onConfirm={() => {
            deleteWebhook({
              variables: {
                webhookItemId: showModal?.id,
                webhookId: webhookId,
              },
            });
          }}
          okButtonProps={{
            loading: deleteWebhookLoading,
          }}
          footer={null}
          closeAction={() => {
            setShowModal(false);
          }}
        />
      )}
    </div>
  );
};
